import React from 'react';
import { Box, FormControl, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { useFormContext, Controller } from 'react-hook-form';

const FieldSet = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: (theme) => theme.spacing(3),
      width: '100%',
      padding: (theme) => theme.spacing(3),
      borderRadius: (theme) => theme.shape.borderRadius,
      backgroundColor: (theme) => theme.palette.background.paper,
    }}
  >
    {children}
  </Box>
);

const BasicInfoStep = ({ user }) => {
  const { control, formState: { errors } } = useFormContext();
  const profileErrors = errors.profile || {};

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="h4" color="text.primary">Basic Information</Typography>
        <Typography color="text.secondary">Let's get to know you better</Typography>
      </Stack>

      <FieldSet>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="profile.firstName"
                control={control}
                defaultValue={user?.given_name || ''}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Enter your first name"
                    fullWidth
                    label="First Name"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!profileErrors.firstName}
                    helperText={profileErrors.firstName?.message}
                    required
                    data-testid="basic-info-first-name"
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="profile.lastName"
                control={control}
                defaultValue={user?.family_name || ''}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Enter your last name"
                    fullWidth
                    label="Last Name"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!profileErrors.lastName}
                    helperText={profileErrors.lastName?.message}
                    required
                    data-testid="basic-info-last-name"
                  />
                )}
              />
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="profile.email"
                control={control}
                defaultValue={user?.email || ''}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="email"
                    placeholder="your.email@example.com"
                    fullWidth
                    label="Email Address"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!profileErrors.email}
                    helperText={profileErrors.email?.message}
                    required
                    data-testid="basic-info-email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="profile.phone"
                control={control}
                defaultValue={user?.phone_number || ''}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="tel"
                    placeholder="04XX XXX XXX"
                    fullWidth
                    label="Phone Number"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!profileErrors.phone}
                    helperText={profileErrors.phone?.message}
                    required
                    data-testid="basic-info-phone"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </FieldSet>
    </Stack>
  );
};

export default BasicInfoStep;